import { IUser } from '@sdk/contracts';
import { useQuery } from '@tanstack/react-query';
import { parseCookies } from '@utils';
import { useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { useCurrentStudentTutorDetail } from '../tutoring-groups';
import { useUserOnboarding } from '../user-onboarding';

import { useMutateProfile } from '.';

import { useSocket } from '@/hooks';
import { posthog } from '@/providers';
import { sdk } from '@/services';
import { checkUserRole } from '@/utils';

const UserService = sdk.getService('UserService');

export const useProfile = () => {
  const { isWorking, updateProfile: mutateProfile } = useMutateProfile();
  const { tutor } = useCurrentStudentTutorDetail();
  const navigateTo = useNavigate();
  const { token } = parseCookies();
  const { connect } = useSocket();
  const prevUserIdRef = useRef<string | undefined>();
  const {
    isLoading,
    data: profile,
    refetch
  } = useQuery({
    queryKey: ['profile', token],
    queryFn: () => UserService.getMe(),
    enabled: !!token,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false
  });

  const { isUserOnboardingCompleted: isOnboardingCompleted, isLoading: isLoadingOnboarding } =
    useUserOnboarding();

  useEffect(() => {
    if (profile) {
      const currentId = profile?._id;
      if (!currentId) return;
      if (prevUserIdRef.current !== currentId) {
        posthog.identify(currentId, {
          email: profile.email,
          name: `${profile.name} ${profile.lastName}`,
          role: profile.role,
          academyId: profile.academy._id,
          academyName: profile.academy.name
        });
      }
      prevUserIdRef.current = currentId;
      connect(profile);
    }
  }, [profile, connect]);

  const roleCheck = checkUserRole(profile);

  const moreInfo = () => UserService.moreInfo();

  const updateProfile = async (updatedUser: Partial<IUser>, onSuccess?: () => void) => {
    mutateProfile(updatedUser, {
      onSuccess: () => onSuccess?.()
    });
  };

  const { isStudent } = roleCheck;

  const shouldRedirectToOnboarding =
    !isLoading && !isLoadingOnboarding && isStudent && !isOnboardingCompleted;

  useEffect(() => {
    if (shouldRedirectToOnboarding) navigateTo('/onboarding');
  }, [shouldRedirectToOnboarding]);

  const profileAcademyTags = useMemo(() => {
    return profile?.tags.filter(tag => tag.academy === profile?.academy._id);
  }, [profile]);

  return {
    isLoading,
    isWorking,
    profile,
    refetch,
    updateProfile,
    roleCheck,
    moreInfo,
    tutor,
    isOnboardingCompleted,
    profileAcademyTags
  };
};
